import { ScreenHeader } from "@/components/common/ScreenHeader";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { BrandButton } from "@/components/ui/brand-button";
import { UserFormFields } from "@/components/user-form";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import authService from "@/lib/auth";
import { showToast } from "@/lib/toast";
import { UserFormData, userSchema } from "@/lib/validation/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, useRouter } from "expo-router";
import React from "react";
import { useForm } from "react-hook-form";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View
} from "react-native";

export default function EditProfileScreen() {
  const { user, setUser, theme } = useAuth();
  const router = useRouter();
  const desktop = useDesktopLayout();

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      first_name: user?.first_name || "",
      middle_name: user?.middle_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      address: user?.address || "",
      origin_city: user?.origin_city || "",
      origin_latitude: Number(user?.origin_latitude) || undefined,
      origin_longitude: Number(user?.origin_longitude) || undefined,
      role: user?.role || "customer",
    },
  });

  const role = watch("role");

  const onSubmit = async (data: UserFormData) => {
    try {
      const { user } = await authService.updateProfile(data);
      setUser(user);
      showToast.success("Profile updated successfully");
      setTimeout(() => {
        router.back();
      }, 1500);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to update profile"
      );
    }
  };

  return (
    <DesktopAppFrame>
      <Stack.Screen options={{
        title: "Edit Profile",
        headerShown: !desktop,
        headerStyle: {
          backgroundColor: theme.background,
        },
        headerTintColor: theme.text,
        headerBackTitle: "Back",
      }}
      />
      {desktop && <ScreenHeader title="Edit Profile" showMenu={false} />}
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={[styles.container, { backgroundColor: theme.background }]}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            desktop && styles.desktopContent,
          ]}
        >
          <View style={styles.form}>
            <UserFormFields
              control={control}
              errors={errors}
              setValue={setValue}
              role={role}
            />

            <BrandButton
              variant="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </BrandButton>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </DesktopAppFrame>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  desktopContent: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
  },
  form: {
    gap: 16,
    padding: 24,
    paddingTop: 20,
  },
});
